import { Injectable } from '@angular/core'
import mjml2html from 'mjml-browser'
import { Observable, of, throwError } from 'rxjs'
import { EmailTheme } from '../interfaces/email-theme.interface'

@Injectable({
  providedIn: 'root'
})
export class MjmlService {
  constructor() {}

  /**
   * Renders MJML to HTML using mjml-browser (in-browser mjml2html)
   */
  renderMjml(mjmlContent: string, theme?: EmailTheme | null, variables?: Record<string, any>): Observable<string> {
    // Process variables first
    const processedMjml = this.processVariables(mjmlContent, theme, variables)
    // Ensure full MJML document
    const fullMjml = this.wrapComponentMjml(processedMjml, theme)

    try {
      const result = mjml2html(fullMjml, {
        beautify: false,
        minify: false,
        validationLevel: 'soft',
        fonts: this.getFontConfiguration(theme)
      } as any)

      if (result.errors && result.errors.length > 0) {
        console.warn('MJML rendering warnings:', result.errors)
      }

      return of(result.html)
    } catch (error: any) {
      const message = error?.message || 'MJML rendering failed in browser'
      return throwError(() => new Error(message))
    }
  }

  /**
   * Compiles MJML to HTML without processing variables.
   * Useful for generating html_template to store alongside mjml_template.
   */
  compileMjmlForStorage(mjmlContent: string): Observable<string> {
    // Wrap into a minimal valid MJML document without injecting theme-specific attributes
    const fullMjml = this.wrapComponentMjml(mjmlContent, null)

    try {
      const result = mjml2html(fullMjml, {
        beautify: false,
        minify: false,
        validationLevel: 'soft'
      } as any)

      if (result.errors && result.errors.length > 0) {
        console.warn('MJML compile warnings:', result.errors)
      }

      return of(result.html)
    } catch (error: any) {
      const message = error?.message || 'MJML compile failed in browser'
      return throwError(() => new Error(message))
    }
  }

  /**
   * Validates MJML syntax without rendering
   */
  validateMjml(mjmlContent: string): Observable<{ valid: boolean; errors: string[] }> {
    // For development: simple validation based on basic MJML structure
    const errors: string[] = []

    // Basic validation checks
    if (!mjmlContent.trim()) {
      errors.push('MJML content is empty')
    }

    if (mjmlContent.includes('<mjml>') && !mjmlContent.includes('</mjml>')) {
      errors.push('Missing closing </mjml> tag')
    }

    if (mjmlContent.includes('<mj-body>') && !mjmlContent.includes('</mj-body>')) {
      errors.push('Missing closing </mj-body> tag')
    }

    // Check for unclosed tags (basic check)
    const openTags = mjmlContent.match(/<mj-[^/>]+>/g) || []
    const closeTags = mjmlContent.match(/<\/mj-[^>]+>/g) || []

    if (openTags.length !== closeTags.length) {
      errors.push('Possible unclosed MJML tags detected')
    }

    return of({
      valid: errors.length === 0,
      errors: errors
    })
  }

  /**
   * Processes theme variables and component variables in MJML content
   */
  private processVariables(mjmlContent: string, theme?: EmailTheme | null, variables?: Record<string, any>): string {
    let processedContent = mjmlContent

    // Replace theme variables if theme is provided
    if (theme?.global_css) {
      const css = theme.global_css

      // Define common variable mappings
      const variableMappings: Record<string, string> = {
        'theme.background_color': css.background_color || '#ffffff',
        'theme.text_color': css.text_color || '#000000',
        'theme.font_size': css.font_size || '16px',
        'theme.font_family': css.font_family || 'Arial, sans-serif',
        'theme.line_height': css.line_height || '1.5',
        'theme.text_alignment': css.text_alignment || 'left',
        'theme.primary_color': css.primary_color || '#007bff',
        'theme.secondary_color': css.secondary_color || '#6c757d',
        'theme.border_radius': css.border_radius || '4px',
        'theme.padding': css.padding || '20px'
      }

      // Replace all variable patterns
      Object.entries(variableMappings).forEach(([variable, value]) => {
        if (value) {
          const regex = new RegExp(`{{\\s*${variable.replace('.', '\\.')}\\s*}}`, 'g')
          processedContent = processedContent.replace(regex, value.toString())
        }
      })

      // Also replace direct property access patterns
      Object.keys(css).forEach((key) => {
        const value = css[key]
        if (value) {
          // Replace {{theme.property}} patterns
          const regex = new RegExp(`{{\\s*theme\\.${key}\\s*}}`, 'g')
          processedContent = processedContent.replace(regex, value.toString())

          // Support legacy color patterns
          if (key.includes('_color')) {
            const colorKey = key.replace('_color', '')
            const colorRegex = new RegExp(`{{\\s*theme\\.colors\\.${colorKey}\\s*}}`, 'g')
            processedContent = processedContent.replace(colorRegex, value.toString())
          }
        }
      })
    }

    // Replace component variables
    if (variables && Object.keys(variables).length > 0) {
      Object.entries(variables).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          // Replace {{component.variableName}} patterns
          const componentRegex = new RegExp(`{{\\s*component\\.${key}\\s*}}`, 'g')
          processedContent = processedContent.replace(componentRegex, value.toString())

          // Also support direct variable patterns {{variableName}}
          const directRegex = new RegExp(`{{\\s*${key}\\s*}}`, 'g')
          processedContent = processedContent.replace(directRegex, value.toString())
        }
      })
    }

    return processedContent
  }

  /**
   * Gets font configuration for MJML based on theme
   */
  private getFontConfiguration(theme?: EmailTheme | null): Record<string, string> {
    const fonts: Record<string, string> = {}

    if (theme?.global_css?.font_family) {
      // Extract font family name for MJML font configuration
      const fontFamily = theme.global_css.font_family
      const fontName = fontFamily.split(',')[0].trim().replace(/['"]/g, '')

      // Add Google Fonts or custom fonts if needed
      if (this.isGoogleFont(fontName)) {
        fonts[fontName] = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`
      }
    }

    return fonts
  }

  /**
   * Checks if a font is a Google Font (simplified check)
   */
  private isGoogleFont(fontName: string): boolean {
    const googleFonts = [
      'Roboto',
      'Open Sans',
      'Lato',
      'Montserrat',
      'Source Sans Pro',
      'Oswald',
      'Raleway',
      'PT Sans',
      'Ubuntu',
      'Merriweather',
      'Nunito',
      'Playfair Display',
      'Poppins',
      'Inter',
      'Work Sans'
    ]

    return googleFonts.some((gFont) => fontName.toLowerCase().includes(gFont.toLowerCase()))
  }

  /**
   * Generates MJML snippets for common components
   */
  getSnippets(): Record<string, string> {
    return {
      section: `<mj-section background-color="{{theme.background_color}}">
  <mj-column>
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      Your content here
    </mj-text>
  </mj-column>
</mj-section>`,

      button: `<mj-button background-color="{{theme.primary_color}}" 
           color="white" 
           border-radius="{{theme.border_radius}}"
           href="#">
  Click Here
</mj-button>`,

      text: `<mj-text font-size="{{theme.font_size}}" 
         color="{{theme.text_color}}" 
         line-height="{{theme.line_height}}">
  {{component.text}}
</mj-text>`,

      image: `<mj-image src="{{component.image_url}}" 
          alt="{{component.alt_text}}" 
          width="{{component.width}}" />`,

      divider: `<mj-divider border-color="{{theme.border_color}}" 
            border-width="1px" 
            border-style="solid" />`,

      spacer: `<mj-spacer height="{{component.height}}" />`,

      hero: `<mj-hero mode="fluid-height" 
         background-width="600px" 
         background-height="400px"
         background-url="{{component.background_image}}"
         background-color="{{theme.primary_color}}"
         padding="100px 0px">
  <mj-text padding="20px" 
           color="white" 
           font-family="{{theme.font_family}}"
           align="center" 
           font-size="45px" 
           line-height="45px">
    {{component.title}}
  </mj-text>
  <mj-button href="{{component.button_url}}" 
             align="center">
    {{component.button_text}}
  </mj-button>
</mj-hero>`,

      twoColumns: `<mj-section background-color="{{theme.background_color}}">
  <mj-column width="50%">
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      Left column content
    </mj-text>
  </mj-column>
  <mj-column width="50%">
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      Right column content
    </mj-text>
  </mj-column>
</mj-section>`,

      threeColumns: `<mj-section background-color="{{theme.background_color}}">
  <mj-column width="33.33%">
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      Column 1
    </mj-text>
  </mj-column>
  <mj-column width="33.33%">
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      Column 2
    </mj-text>
  </mj-column>
  <mj-column width="33.33%">
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      Column 3
    </mj-text>
  </mj-column>
</mj-section>`
    }
  }

  /**
   * Wraps component MJML in a complete MJML document for testing
   */
  wrapComponentMjml(componentMjml: string, theme?: EmailTheme | null): string {
    // Check if the component MJML already contains a complete MJML document
    const trimmedContent = componentMjml.trim()
    const isCompleteDocument = trimmedContent.startsWith('<mjml>') && trimmedContent.endsWith('</mjml>')

    if (isCompleteDocument) {
      // If it's already a complete document, return as-is (variables should already be processed)
      return componentMjml
    }

    // Otherwise, wrap the component content in a complete MJML structure
    const themeStyles = this.getThemeStyles(theme)

    return `
      <mjml>
        <mj-head>
          ${themeStyles}
          <mj-attributes>
            <mj-all font-family="${theme?.global_css?.font_family || 'Arial, sans-serif'}" />
            <mj-text font-size="${theme?.global_css?.font_size || '16px'}" 
                     line-height="${theme?.global_css?.line_height || '1.5'}" 
                     color="${theme?.global_css?.text_color || '#000000'}" />
            <mj-section background-color="${theme?.global_css?.background_color || '#ffffff'}" 
                        padding="${theme?.global_css?.padding || '20px'}" />
          </mj-attributes>
        </mj-head>
        <mj-body background-color="${theme?.global_css?.background_color || '#ffffff'}">
          ${componentMjml}
        </mj-body>
      </mjml>
    `
  }

  /**
   * Generates theme-specific styles for MJML head
   */
  private getThemeStyles(theme?: EmailTheme | null): string {
    if (!theme?.global_css) return ''

    const css = theme.global_css
    const styles = []

    // Add custom CSS classes for theme colors
    if (css.primary_color) {
      styles.push(`
        .primary-color { color: ${css.primary_color} !important; }
        .primary-bg { background-color: ${css.primary_color} !important; }
      `)
    }

    if (css.secondary_color) {
      styles.push(`
        .secondary-color { color: ${css.secondary_color} !important; }
        .secondary-bg { background-color: ${css.secondary_color} !important; }
      `)
    }

    if (css.border_radius) {
      styles.push(`
        .rounded { border-radius: ${css.border_radius} !important; }
      `)
    }

    // Add responsive styles
    styles.push(`
      @media only screen and (max-width: 480px) {
        .mobile-hide { display: none !important; }
        .mobile-center { text-align: center !important; }
        .mobile-full-width { width: 100% !important; }
      }
    `)

    return styles.length > 0 ? `<mj-style>${styles.join('\n')}</mj-style>` : ''
  }
}
